/**
 * /projects/:projectId/pipelines/new — create a pipeline inside a project.
 * A new pipeline starts in Development with an empty graph; on success
 * navigates back to the project detail page.
 */

import { useState } from "react";
import { Button, Group, Paper, Stack, Textarea, TextInput, Title } from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { createPipeline, type CreatePipelineIn } from "@/shared/api/projects";

export function NewPipeline() {
  const { projectId = "" } = useParams();
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const valid = name.trim().length > 0;

  const onSave = async () => {
    setBusy(true);
    try {
      const token = await getToken();
      const input: CreatePipelineIn = {
        name: name.trim(),
        description: description.trim() || null,
      };
      const created = await createPipeline(token, projectId, input);
      toastSuccess("Pipeline created", `${created.name} is in Development.`);
      navigate(`/projects/${projectId}`);
    } catch (err) {
      toastApiError("Could not create pipeline", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="md" maw={640}>
      <Title order={2}>New pipeline</Title>
      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Ingest & classify"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Description"
            placeholder="Optional"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={() => void onSave()}
              loading={busy}
              disabled={!valid || busy}
            >
              Create pipeline
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
